import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle, Loader2, Users } from 'lucide-react';
import { useActiveConsultantsForRedistribution } from '@/hooks/useActiveConsultantsForRedistribution';
import { useDeactivateUserWithRedistribution } from '@/hooks/useDeactivateUserWithRedistribution';

interface DeactivateUserModalProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  userName: string;
  dealCount: number;
  onConfirmed: () => void;
}

export function DeactivateUserModal({
  open,
  onClose,
  userId,
  userName,
  dealCount,
  onConfirmed,
}: DeactivateUserModalProps) {
  const { data: consultants = [], isLoading } = useActiveConsultantsForRedistribution(open);
  const eligible = consultants.filter((c) => c.id !== userId);
  const mutation = useDeactivateUserWithRedistribution();

  const perConsultant = eligible.length > 0 ? Math.ceil(dealCount / eligible.length) : 0;
  const canConfirm = eligible.length > 0 && !mutation.isPending;

  const handleConfirm = async () => {
    try {
      await mutation.mutateAsync(userId);
      onConfirmed();
      onClose();
    } catch {
      // erro já tratado pelo toast no hook
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !mutation.isPending && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            Inativar {userName}
          </DialogTitle>
          <DialogDescription>
            Este usuário possui <strong>{dealCount}</strong>{' '}
            {dealCount === 1 ? 'deal ativo' : 'deals ativos'} como Consultor de Vendas.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : eligible.length === 0 ? (
            <Alert variant="destructive">
              <AlertTriangle className="w-4 h-4" />
              <AlertDescription>
                Não há Consultores de Vendas ativos disponíveis para receber os deals. Ative pelo
                menos um Consultor antes de inativar este usuário.
              </AlertDescription>
            </Alert>
          ) : (
            <>
              <div className="text-sm text-muted-foreground">
                Os deals serão redistribuídos automaticamente (round-robin) entre os Consultores
                ativos:
              </div>
              <div className="rounded-lg border bg-muted/30 p-4 space-y-2">
                {eligible.map((c) => (
                  <div key={c.id} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-muted-foreground" />
                      <span className="font-medium">{c.full_name}</span>
                    </div>
                    <span className="text-muted-foreground">~{perConsultant} deals</span>
                  </div>
                ))}
              </div>
              <div className="text-xs text-muted-foreground">
                Deals com stage <strong>WON</strong> ou <strong>LOST</strong> não serão tocados.
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button onClick={handleConfirm} disabled={!canConfirm}>
            {mutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Redistribuindo...
              </>
            ) : (
              'Confirmar e Inativar'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
